import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { FieldLabel } from "../primitives/FieldLabel";
import { TextField } from "../primitives/TextField";
import { PasswordRules } from "./PasswordRules";
import { RevealPasswordToggle } from "./RevealPasswordToggle";
import { authFormStyles } from "./authFormStyles";
import type { UiTheme } from "../theme";

// Il blocco «inventa una password»: il campo, le regole vive, la conferma, la
// riga che dice se le due combaciano e l'interruttore che le scopre entrambe.
//
// ⛔ Perché nasce ORA. Il pattern esisteva già intero in `WelcomeForm`, la
// porta d'invito. `PROF-5` porta la conferma anche sulla porta di RECUPERO —
// che è il secondo posto dell'app dove una password si inventa — e copiarcelo
// avrebbe fatto due copie che possono divergere: la riga viva è proprio il
// genere di dettaglio che si aggiusta su una superficie sola.
//
// ⚠️ `ChangePasswordCard` (mobile) fa lo stesso confronto e NON entra qui
// oggi: valida con `newPw.length >= 10` invece della policy condivisa
// `unmetPasswordRules`, quindi diventerebbe un chiamante che si porta dentro
// una regola diversa. Prima migra alla policy (nominato in
// `next/consolidation.md`), poi è il terzo callsite pulito.

export type PasswordMatch = "empty" | "match" | "differ";

/** Lo stato del confronto, in UN posto: lo legge sia la riga che si vede sia
 *  la validazione del form, che altrimenti potrebbero dire cose diverse sullo
 *  stesso paio di password. ⛔ `empty` non è «differ»: rimproverare chi non ha
 *  ancora digitato la conferma è un rimprovero per una cosa non fatta. */
export const passwordMatch = (
  password: string,
  confirm: string,
): PasswordMatch =>
  confirm.length === 0 ? "empty" : confirm === password ? "match" : "differ";

export type NewPasswordFieldsProps = {
  theme: UiTheme;
  password: string;
  confirm: string;
  onChangePassword: (next: string) => void;
  onChangeConfirm: (next: string) => void;
  editable?: boolean;
  /** Se i due campi sono in chiaro. ⛔ CONTROLLATO dal chiamante, non tenuto
   *  qui dentro: il form è l'unico che sa quando si è premuto «invia», e la
   *  garanzia è che a ogni tentativo le password tornino coperte — anche
   *  quando il tentativo si ferma sulla validazione e la rete non parte mai.
   *  Tenendo lo stato qui quella garanzia si era ristretta al solo giro di
   *  rete, in silenzio: l'ha alzata la review, non un test. */
  revealed: boolean;
  onToggleReveal: () => void;
  /** Quale dei due campi è in errore. `null` = nessuno: marcarli entrambi per
   *  una password corta direbbe che anche la conferma è sbagliata. */
  badField?: "password" | "confirm" | null;
};

export function NewPasswordFields({
  theme,
  password,
  confirm,
  onChangePassword,
  onChangeConfirm,
  editable = true,
  revealed,
  onToggleReveal,
  badField = null,
}: NewPasswordFieldsProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  // DS-67 — UN interruttore per ENTRAMBI i campi; il perché sta sul primitivo
  // che lo disegna (`RevealPasswordToggle`).
  const match = passwordMatch(password, confirm);

  return (
    <View style={styles.block}>
      <View>
        <FieldLabel theme={theme} style={styles.label}>
          {t("auth.newPassword")}
        </FieldLabel>
        <TextField
          theme={theme}
          accessibilityLabel={t("auth.newPassword")}
          // Come sulla porta di accesso: r-n-w mette `sentences` di default,
          // che su un telefono maiuscola la prima lettera di una password.
          autoCapitalize="none"
          autoComplete="new-password"
          autoCorrect={false}
          editable={editable}
          invalid={badField === "password"}
          placeholder="•••••••••"
          secureTextEntry={!revealed}
          style={styles.field}
          textContentType="newPassword"
          value={password}
          onChangeText={onChangePassword}
        />
      </View>

      {/* ⛔ Prende il posto della frase statica `auth.passwordHint`, non le si
          affianca: la stessa informazione due volte, una delle quali non sa mai
          dire se è soddisfatta, è la versione peggiore di sé. */}
      <PasswordRules theme={theme} password={password} />

      <View>
        <FieldLabel theme={theme} style={styles.label}>
          {t("auth.confirmPassword")}
        </FieldLabel>
        <TextField
          theme={theme}
          accessibilityLabel={t("auth.confirmPassword")}
          autoCapitalize="none"
          autoComplete="new-password"
          autoCorrect={false}
          editable={editable}
          invalid={badField === "confirm"}
          placeholder="•••••••••"
          secureTextEntry={!revealed}
          style={styles.field}
          textContentType="newPassword"
          value={confirm}
          onChangeText={onChangeConfirm}
        />
      </View>

      {match !== "empty" ? (
        <Text
          style={[
            styles.rule,
            match === "match" ? styles.ruleMet : styles.ruleDiffer,
          ]}
          accessibilityLiveRegion="polite"
        >
          {match === "match"
            ? `✓  ${t("auth.passwordsMatch")}`
            : `·  ${t("auth.passwordMismatch")}`}
        </Text>
      ) : null}

      {/* ⛔ L'interruttore sta SOTTO i due campi, non dentro il primo: agisce su
          entrambi, e metterlo dentro uno direbbe che riguarda solo quello. */}
      <RevealPasswordToggle
        theme={theme}
        revealed={revealed}
        onToggle={onToggleReveal}
        count={2}
      />
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return {
    ...authFormStyles(theme),
    ...StyleSheet.create({
      // La colonna dei form mette `gap` fra i figli diretti; questo blocco ne è
      // uno solo, quindi si ridà la stessa spaziatura al suo interno.
      block: { gap: s.s4 },
      // `base` e non `sm`: sono righe che l'atleta DEVE leggere per andare
      // avanti, non decorazione. Il contrasto avanzava (ink3 su bg è 7.04:1).
      rule: { color: p.ink3, fontSize: ty.sizes.base },
      // ⛔ Il colore ARRIVA DOPO il segno, non al posto suo: `✓` contro `·`
      // regge da solo in monocromia, e la tinta rinforza per chi la vede.
      ruleMet: { color: p.ink2 },
      ruleDiffer: { color: p.danger },
    }),
  };
}
